<script lang="ts">
  import PubSub from 'pubsub-js'
  import Swal from 'sweetalert2'
  import { onMount } from 'svelte'
  import { get } from 'svelte/store'
  import { lang } from '@/store'
  import { i18n, showErrorMessage } from '@/plugins/util'
  import Navbar from '@/components/appDriver/Navbar.svelte'
  import { currentPage } from '@/components/appDriver/store'
  import PageWallet from '@/components/appDriver/wallet/PageWallet.svelte'
  import WalletAmount from '@/components/appDriver/wallet/WalletAmount.svelte'
  import { getWalletDetail, payoutToDriverByBankTransfer } from '@/components/appDriver/wallet/service'
  import { cachedWalletSummary, setCachedWalletSummary } from '@/components/appDriver/wallet/store'
  import type { WalletBankTransferRequest, WalletBankTransferResult, WalletSummary } from '@/components/appDriver/wallet/types'
  import {
    formatWalletAmountInput,
    getBankTransferConfirmHtml,
    getBankTransferSuccessHtml,
    getOptionalDriverLocation,
    getWalletRequestErrorMessage,
    parseWalletAmountInput,
    shouldBlockWalletAmountKeydown,
  } from '@/components/appDriver/wallet/pageHelpers'
  import { generateDriverWithdrawIdempotencyKey, getWalletActionState, maskBankAccountNumber } from '@/components/appDriver/wallet/utils'

  const MAX_WITHDRAW_AMOUNT_DIGITS = 8
  const MAX_WITHDRAW_INPUT_AMOUNT = 99999999

  let walletSummary: WalletSummary | null = null
  let amount = 0
  let amountInput = ''
  let isLoadingWalletDetail = false
  let isSubmittingWithdraw = false
  let errorMessage = ''

  $: actionState = getWalletActionState(walletSummary?.balance ?? 0)
  $: hasBankDetails = !!walletSummary?.isBankDetailsEnable && (walletSummary.accountHolderName || walletSummary.accountNumber)

  onMount(() => {
    $currentPage = 'wallet-withdraw'
    const cachedSummary = get(cachedWalletSummary)

    if (cachedSummary) {
      applyWalletSummary(cachedSummary)
      return
    }

    void fetchWalletDetail()
  })

  function handleAmountInput(event: Event) {
    const input = event.currentTarget as HTMLInputElement

    amount = parseWalletAmountInput(input.value, MAX_WITHDRAW_AMOUNT_DIGITS)
    amountInput = formatWalletAmountInput(amount, MAX_WITHDRAW_INPUT_AMOUNT)
  }

  function handleAmountKeydown(event: KeyboardEvent) {
    if (shouldBlockWalletAmountKeydown(event, MAX_WITHDRAW_AMOUNT_DIGITS)) {
      event.preventDefault()
    }
  }

  async function fetchWalletDetail() {
    isLoadingWalletDetail = true
    errorMessage = ''

    try {
      const latestWalletSummary = await getWalletDetail({
        lan: $lang,
      })
      setCachedWalletSummary(latestWalletSummary)
      applyWalletSummary(latestWalletSummary)
    } catch (error) {
      walletSummary = null
      amount = 0
      amountInput = ''
      errorMessage = getWalletRequestErrorMessage(error, $lang, i18n($lang, ['Cannot load wallet detail', 'ບໍ່ສາມາດໂຫຼດຂໍ້ມູນກະເປົາເງິນໄດ້']))
      showErrorMessage(errorMessage)
    } finally {
      isLoadingWalletDetail = false
    }
  }

  function getConfirmWithdrawHtml(summary: WalletSummary): string {
    return getBankTransferConfirmHtml(summary, amount, $lang)
  }

  async function submitWithdraw() {
    const availableWithdrawAmount = actionState.withdrawableAmount

    if (
      isSubmittingWithdraw
      || isLoadingWalletDetail
      || !walletSummary
      || amount <= 0
      || amount > availableWithdrawAmount
      || !actionState.canWithdraw
    ) {
      if (walletSummary && amount > availableWithdrawAmount) {
        showErrorMessage(i18n($lang, [
          'Amount cannot be higher than available balance.',
          'ຈຳນວນເງິນຕ້ອງບໍ່ຫຼາຍກວ່າຍອດທີ່ຖອນໄດ້.',
        ]))
      }

      return
    }

    const confirmResult = await Swal.fire({
      icon: 'question',
      title: i18n($lang, ['Confirm withdrawal?', 'ຢືນຢັນຖອນເງິນ?']),
      html: getConfirmWithdrawHtml(walletSummary),
      showCancelButton: true,
      confirmButtonText: i18n($lang, ['Confirm', 'ຢືນຢັນ']),
      cancelButtonText: i18n($lang, ['Cancel', 'ຍົກເລີກ']),
      confirmButtonColor: '#1CAAFC',
      cancelButtonColor: '#6B7280',
    })

    if (!confirmResult.isConfirmed) {
      return
    }

    isSubmittingWithdraw = true

    try {
      void Swal.fire({
        title: i18n($lang, ['Submitting request...', 'ກຳລັງສົ່ງຄຳຂໍ...']),
        text: i18n($lang, [
          'Please wait. Do not close this screen.',
          'ກະລຸນາລໍຖ້າ ແລະ ຢ່າປິດໜ້ານີ້.',
        ]),
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading()
        },
      })

      const location = await getOptionalDriverLocation()
      const request: WalletBankTransferRequest = {
        direction: 'payout_to_driver',
        amount: Math.trunc(amount),
        idempotency_key: generateDriverWithdrawIdempotencyKey(),
      }

      if (location) {
        Object.assign(request, {
          latitude: `${location.latitude}`,
          longitude: `${location.longitude}`,
        })
      }

      const transferResult = await payoutToDriverByBankTransfer(request)

      const latestWalletSummary = await getWalletDetail({
        lan: $lang,
      })
      setCachedWalletSummary(latestWalletSummary)
      Swal.close()

      await Swal.fire({
        icon: 'success',
        title: i18n($lang, ['Withdrawal Successful', 'ຖອນເງິນສຳເລັດ']),
        html: getTransferSuccessHtml(transferResult),
        confirmButtonText: i18n($lang, ['Done', 'ສຳເລັດ']),
        confirmButtonColor: '#1CAAFC',
      })

      PubSub.publish('DRIVER.CHANGE_PAGE', { component: PageWallet })
    } catch (error) {
      Swal.close()
      const submitErrorMessage = getWalletRequestErrorMessage(error, $lang, i18n($lang, ['Cannot submit withdrawal request', 'ບໍ່ສາມາດສົ່ງຄຳຂໍຖອນເງິນໄດ້']))
      showErrorMessage(submitErrorMessage)
    } finally {
      isSubmittingWithdraw = false
    }
  }

  function getTransferSuccessHtml(result: WalletBankTransferResult): string {
    return getBankTransferSuccessHtml(result, i18n($lang, [
      'Your withdrawal to bank account is complete.',
      'ການຖອນເງິນເຂົ້າບັນຊີທະນາຄານສຳເລັດແລ້ວ.',
    ]))
  }

  function applyWalletSummary(summary: WalletSummary) {
    walletSummary = summary
    amount = 0
    amountInput = ''
  }

</script>

<div class="page flex min-h-full flex-col bg-[#EFEFEF]">
  <Navbar showBack={true} on:back={() => PubSub.publish('DRIVER.CHANGE_PAGE', { component: PageWallet })}/>

  <img src="{new URL('@/assets/general/loca-logo-horizontal.png', import.meta.url).href}" width="100" class="-mt-10 mx-auto" alt="Loca">

  <div class="m-4 mt-6 text-center text-xl font-semibold">
    {i18n($lang, ['Withdraw Now', 'ຖອນເງິນ'])}
  </div>

  <section class="mx-4 rounded-lg bg-white p-4 shadow">
    <div class="text-sm font-semibold uppercase text-gray-400">
      {i18n($lang, ['Available to Withdraw', 'ຈຳນວນທີ່ຖອນໄດ້'])}
    </div>
    {#if isLoadingWalletDetail}
      <div class="mt-3 h-10 w-48 animate-pulse rounded bg-gray-200"></div>
      <div class="mt-4 h-4 w-full animate-pulse rounded bg-gray-100"></div>
      <div class="mt-2 h-4 w-4/5 animate-pulse rounded bg-gray-100"></div>
    {:else if walletSummary}
      <div class="mt-2 text-4xl font-bold text-green-600"><WalletAmount amount={actionState.withdrawableAmount} symbol={walletSummary.currencySymbol}/></div>
      <p class="mt-3 text-sm leading-6 text-gray-500">
        {i18n($lang, [
          'This amount comes from the latest wallet detail.',
          'ຈຳນວນນີ້ມາຈາກຂໍ້ມູນກະເປົາເງິນລ່າສຸດ.',
        ])}
      </p>
    {:else}
      <div class="mt-2 text-sm text-red-600">{errorMessage}</div>
      <button
        type="button"
        class="mt-4 rounded-lg bg-blue-primary px-5 py-2.5 text-sm font-bold uppercase text-white shadow-sm"
        on:click={fetchWalletDetail}
      >
        {i18n($lang, ['Try Again', 'ລອງໃໝ່'])}
      </button>
    {/if}
  </section>

  <section class="m-4 rounded-lg bg-white p-4 shadow">
    <h2 class="text-lg font-bold text-blue-dark">{i18n($lang, ['Withdraw To Bank', 'ຖອນເຂົ້າທະນາຄານ'])}</h2>

    <div class="mt-5 flex w-full items-center gap-4 border-b border-gray-200 pb-4 text-left">
      <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-green-100 text-green-600">
        <svg viewBox="0 0 24 24" class="h-7 w-7" aria-hidden="true">
          <path fill="currentColor" d="M12 3 3 8v2h18V8l-9-5Zm-6 9v6H4v2h16v-2h-2v-6h-2v6h-3v-6h-2v6H8v-6H6Z" />
        </svg>
      </div>
      <div class="min-w-0 flex-1">
        <div class="text-xs uppercase text-gray-400">{i18n($lang, ['Bank Account', 'ບັນຊີທະນາຄານ'])}</div>
        {#if hasBankDetails}
          {#if walletSummary?.accountHolderName}
            <div class="truncate text-lg font-medium text-gray-700">{walletSummary.accountHolderName}</div>
          {/if}
          {#if walletSummary?.accountNumber}
            <div class="mt-0.5 text-sm text-gray-500">{maskBankAccountNumber(walletSummary.accountNumber)}</div>
          {/if}
        {:else}
          <div class="truncate text-lg font-medium text-gray-600">{i18n($lang, ['No bank account available', 'ບໍ່ມີບັນຊີທະນາຄານ'])}</div>
        {/if}
      </div>
    </div>

    <label class="mt-6 block">
      <span class="text-lg font-bold text-blue-dark">{i18n($lang, ['Withdraw Amount', 'ຈຳນວນທີ່ຖອນ'])}</span>
      <div class="mt-4 flex items-end gap-3">
        <span class="text-5xl leading-none text-gray-700">₭</span>
        <input
          type="text"
          inputmode="numeric"
          value={amountInput}
          disabled={isLoadingWalletDetail || isSubmittingWithdraw || !walletSummary}
          class="w-full border-0 border-b border-gray-500 px-0 pb-2 text-3xl font-semibold text-gray-700 placeholder:text-gray-300 focus:border-blue-primary focus:ring-0"
          placeholder="ປ້ອນຈຳນວນ"
          on:input={handleAmountInput}
          on:keydown={handleAmountKeydown}
        >
      </div>
    </label>
  </section>

  {#if amount > actionState.withdrawableAmount}
    <section class="m-4 rounded-md bg-red-50 px-4 py-4 text-sm text-red-600">
      {i18n($lang, ['Amount cannot be higher than available balance.', 'ຈຳນວນເງິນຕ້ອງບໍ່ຫຼາຍກວ່າຍອດທີ່ຖອນໄດ້.'])}
    </section>
  {/if}

  <div class="mt-auto">
    <button
      type="button"
      class="w-full bg-blue-dark px-6 py-5 text-xl font-bold uppercase text-white disabled:bg-gray-300"
      disabled={isLoadingWalletDetail || isSubmittingWithdraw || !walletSummary || amount <= 0 || amount > actionState.withdrawableAmount || !actionState.canWithdraw}
      on:click={submitWithdraw}
    >
      {#if isSubmittingWithdraw}
        {i18n($lang, ['Processing...', 'ກຳລັງດຳເນີນການ...'])}
      {:else}
        {i18n($lang, ['Confirm Withdraw', 'ຢືນຢັນການຖອນ'])}
      {/if}
    </button>
  </div>
</div>
